import type { Session, SessionStorage } from '@repo/api-client/react';
import { logger } from '@repo/logger';

/**
 * localStorage-backed session storage for the web app.
 *
 * The bearer access + refresh tokens are owned by SuperTokens via
 * HttpOnly cookies, so we don't store them here. This storage only
 * caches the identity strings (`userId`, `email`, `displayName`) so
 * the shell can render before the `/auth/me` round-trip resolves on
 * cold load.
 *
 * SSR safety: every read/write goes through `safeWindow()` so the
 * module never crashes during a SSR pre-render or in test
 * environments where `window` isn't present. Same defensive shape as
 * `apps/mobile/session/secureSessionStorage.ts`.
 */
const KEY = 'invoice-studio.session.v1';

function safeWindow(): Window | null {
  if (typeof window === 'undefined') return null;
  try {
    void window.localStorage;
    return window;
  } catch {
    return null;
  }
}

export function webSessionStorage(): SessionStorage {
  return {
    async read(): Promise<Session | null> {
      const w = safeWindow();
      if (!w) return null;
      try {
        const raw = w.localStorage.getItem(KEY);
        if (!raw) return null;
        return JSON.parse(raw) as Session;
      } catch (err) {
        logger.warn('webSessionStorage.read failed', { error: err });
        return null;
      }
    },
    async write(session: Session): Promise<void> {
      const w = safeWindow();
      if (!w) return;
      try {
        w.localStorage.setItem(KEY, JSON.stringify(session));
      } catch (err) {
        logger.warn('webSessionStorage.write failed', { error: err });
      }
    },
    async clear(): Promise<void> {
      const w = safeWindow();
      if (!w) return;
      try {
        w.localStorage.removeItem(KEY);
      } catch (err) {
        logger.warn('webSessionStorage.clear failed', { error: err });
      }
    },
  };
}
